// My Accounts — list view of facilities for the current AM.
// Default test view: Landon's Kansas City accounts (filter applied when no
// employee context is available, e.g. running locally before sign-in lookup
// completes). For real usage, the filter is `account_manager_eid = my eid`.
//
// Each card (rendered via the shared FacilityCard component) shows:
//   • Market stripe (Missouri/Texas/Colorado/KC/STL/FL accent)
//   • RAG dot + facility name + lifecycle pill (when not Active)
//   • FAC-XXXX · type · address summary
//   • Latest sentiment pill on the right
//   • Meta line: AM · monthly rate · open feedback count
//
// Why we compute open feedback live: the denormalised counter columns on
// facilities (open_feedback_count / open_task_count / last_cleaning_date /
// last_feedback_date) were dropped on 2026-05-19 because they were never
// maintained by any trigger and were stale on every facility. The UI now
// reads the truth from facility_feedback / facility_tasks directly.
//
// Map view is LIVE (WS8 2026-07-31) — shared <FacilityMap> primitive scoped
// to the current list filter. Placeholder retired.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { List as ListIcon, Map as MapIcon, Search, X } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { useFacilityScope, FacilityScopeControl } from '../../shared/scope';
import {
  Button,
  EmptyState,
  ErrorState,
  FacilityCard,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { shortAddress } from '../../shared/format';
import { RAG_COLORS } from '../../shared/design-tokens';
import { supabase } from '../../shared/supabase';
import { lensFor, startLabel } from '../../shared/building-lifecycle';
import { fetchAmNameMap } from '../../shared/am-name';
import type {
  Facility,
  FacilityFeedback,
  FeedbackSentiment,
  RagCategory,
  RagStatus,
} from '../../shared/types';
import { RagEditPopover } from './components/rag-edit-popover';
// Red -> Amber -> Green -> unset, then alphabetical. Shared by BOTH load paths
// below so the card view and the "Show closed" audit view cannot disagree.
// The sort is client-side because PostgREST `.order()` takes a COLUMN, not an
// expression — see lib/rag-order.ts for why there is no rag_rank column.
import { sortByRagThenName } from './lib/rag-order';
// Moved out of this file by CW-Q Phase 6 so it can be server-rendered in a test.
import { FacilityMetaLine } from './components/facility-meta-line';
import { runningHourlyRate } from '../../shared/rate-basis';
import { useFacilitiesCleaning } from './hooks/use-facility-cleaning';
import { FacilityMap } from '../../shared/map';
import { errorMessage } from '../../shared/errors';
type ViewMode = 'list' | 'map';
type RagFilter = 'all' | RagStatus;
// Which facility's RAG is being edited from the list. null = no popover open.
// (Financial dimension removed 2026-07-06 — operational only.)
type EditingRag = { facilityId: string } | null;

// State-name → state-code alias map. Lets the search box match "colorado"
// to all CO facilities, "missouri" to all MO, etc. Covers every state we
// currently operate in. Extend when a new market opens.
const STATE_ALIASES: Record<string, string> = {
  colorado: 'CO',
  missouri: 'MO',
  kansas: 'KS',
  texas: 'TX',
  nebraska: 'NE',
  nevada: 'NV',
  iowa: 'IA',
  florida: 'FL',
};

interface FacilityWithSentiment extends Facility {
  latest_sentiment: FeedbackSentiment | null;
  /**
   * CW-Q Phase 6 — `v_facility_margins.rate_basis` for this row, or null when
   * the view has no row for it (Closed / Merged are excluded from the view) or
   * the read failed. Optional: a brand-new building has no margin row yet and
   * should say nothing about rates rather than guess.
   *
   * Why the list needed this at all: FacilityMetaLine rendered
   * `fmtCurrency(monthly_rate)`, which returns '' for NULL, so all 14 hourly
   * hospitals showed NO rate on the card an AM actually scans — silently
   * identical to a building nobody has rated.
   */
  live_rate_basis?: string | null;
  /** Items 831/758: the running hourly lines' one hourly rate (fetchHourlyRateMap). */
  live_hourly_rate?: number | null;
  live_open_feedback_count: number;
  // CW-I Phase 2 — total, not just open. See the enrichment loop for why.
  live_total_feedback_count: number;
  account_name: string | null;
  /** building_lifecycle() answer from the card views. null for a closed building. */
  lifecycle?: string | null;
}

// Postgrest embed shape returned by the joined query below. We unwrap this
// into a flat `account_name` on the in-memory record before rendering.
interface FacilityRowFromDb extends Facility {
  accounts: { account_name: string | null } | null;
}

// AFB-0039 — row shape from v_am_account_cards: one row per live building,
// with account_name, AM display name, and the per-facility feedback summary
// (latest sentiment + open count) folded in server-side.
type AmAccountCardRow = Facility & {
  account_name: string | null;
  account_manager_name: string | null;
  latest_sentiment: FeedbackSentiment | null;
  open_feedback_count: number | null;
  lifecycle: string | null;
};

/**
 * CW-Q Phase 6 — facility_id → rate_basis, for the list view.
 *
 * A second round trip rather than a join, deliberately. `v_am_account_cards`
 * exists so the default list is ONE read; folding v_facility_margins into it
 * would drag that view's per-facility aggregate and clusters CTE into every
 * list render. And deriving the basis here from billing_type would put the rule
 * in a second place — v_facility_margins is the only thing that knows about
 * billing_type AND client_billing_lines, so it stays the only thing that
 * decides. ~210 rows; the `.in()` is keyed on ids we already have.
 *
 * Non-fatal on error: an empty map means every row falls back to
 * `deriveRateBasisFallback`, which never claims 'hourly'.
 */
async function fetchRateBasisMap(ids: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  if (ids.length === 0) return out;
  const { data, error } = await supabase
    .from('v_facility_margins')
    .select('facility_id, rate_basis')
    .in('facility_id', ids);
  if (error) return out;
  for (const r of (data ?? []) as Array<{
    facility_id: string | null;
    rate_basis: string | null;
  }>) {
    if (r.facility_id && r.rate_basis) out.set(r.facility_id, r.rate_basis);
  }
  return out;
}

/**
 * Items 831/758 — facility_id → the hourly rate of its running lines billed by
 * the hour, so an hourly building reads "Billed on actual hours — $26 an hour".
 * Read from the LINES (runningHourlyRate), never the building's billing type; a
 * building whose hourly lines disagree gets no entry and just reads "Billed on
 * actual hours". Non-fatal on error, like the basis map: no figure, never a guess.
 */
async function fetchHourlyRateMap(ids: string[]): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (ids.length === 0) return out;
  const { data, error } = await supabase
    .from('service_assignments')
    .select('facility_id, billing_method, billing_rate, assignment_status')
    .in('facility_id', ids)
    .eq('assignment_status', 'Active')
    .eq('billing_method', 'Hourly');
  if (error) return out;
  const byFacility = new Map<string, Array<{ billing_method: string | null; billing_rate: number | null; assignment_status: string | null }>>();
  for (const r of (data ?? []) as Array<{ facility_id: string; billing_method: string | null; billing_rate: number | null; assignment_status: string | null }>) {
    const list = byFacility.get(r.facility_id) ?? [];
    list.push({ ...r, billing_rate: r.billing_rate == null ? null : Number(r.billing_rate) });
    byFacility.set(r.facility_id, list);
  }
  for (const [id, lines] of byFacility) {
    const rate = runningHourlyRate(lines);
    if (rate != null) out.set(id, rate);
  }
  return out;
}

// 2026-05-29 — alias-aware search via the server-side resolver.
// Row shape returned by the search_entities(p_query, p_entity_types, p_limit)
// RPC. We only consume facility rows here. score: 0 exact, 1 prefix, 2
// substring (lower = better). match_kind tells us whether the hit came from
// the facility name or an entry in entity_aliases.
interface SearchEntityRow {
  entity_type: string;
  entity_id: string;
  display_name: string;
  account_id: string | null;
  facility_type: string | null;
  status: string | null;
  matched_on: string;
  match_kind: 'name' | 'alias';
  score: number;
  // Full candidate count before the LIMIT (#8) — lets the box say "X of Y".
  total_matches: number;
}

// Per-facility match metadata we keep client-side after a resolver call.
interface AliasMatch {
  matchKind: 'name' | 'alias';
  matchedOn: string;
  score: number;
}

// NEW ACCOUNTS lens for a facility — READ from the database (2026-09-25).
// The `lifecycle` column (public.building_lifecycle) decides starting soon /
// recently started / behind; a confirmed Active building always beats its
// planned date. lensFor() only maps that answer to a pill. See
// shared/building-lifecycle.ts.

type LifecycleFilter = 'all' | 'starting_soon' | 'recent';

export default function MyAccounts() {
  const { employee, profile } = useAuth();
  // CW-A Phase 2 (2026-08-04) — scope is no longer implied by role. It is an
  // explicit, persisted, user-visible choice shared with /map via
  // useFacilityScope, so the two surfaces cannot answer "which facilities"
  // differently again. Default: assigned if any, else ALL.
  const {
    scope,
    setScope,
    myFacilityIds,
    canScopeToMine,
    resolved: scopeResolved,
  } = useFacilityScope();
  const [allFacilityCount, setAllFacilityCount] = useState<number | null>(null);
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleFilter>('all');
  const [facilities, setFacilities] = useState<FacilityWithSentiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');
  // 2026-05-29 — alias-aware search. `search` is the immediate input value;
  // `debouncedTerm` lags ~200ms and drives the server-side resolver call.
  // `searchMatches` holds the resolver's per-facility match metadata keyed by
  // facility_id (null = no active search). The resolver (search_entities RPC)
  // matches BOTH facility names and entity_aliases — e.g. "OPR" → Overland
  // Park Regional — and is the same primitive Voice-to-Text will use, so the
  // box calls it rather than reimplementing alias matching client-side.
  // We still UNION the resolver hits with the existing local text filter
  // (address / AM name / state-name aliases) so no prior search field
  // regresses; the resolver purely ADDS alias + ranked name matching.
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [searchMatches, setSearchMatches] = useState<Map<
    string,
    AliasMatch
  > | null>(null);
  // #8 — resolver total vs returned, so we can show "showing N of M" when the
  // server-side search is truncated (capped at 25).
  const [searchTrunc, setSearchTrunc] = useState<{ shown: number; total: number } | null>(null);
  const [ragFilter, setRagFilter] = useState<RagFilter>('all');
  // 2026-05-12: closed-facility toggle. Off by default — the operational
  // AM roster runs off the active_facilities view (Closed/Cancelled
  // excluded). Flipping On switches the source table to base `facilities`
  // so the user can audit recently-closed accounts or look up their
  // post-mortems without leaving the module.
  const [showClosed, setShowClosed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // 2026-05-21 — click-to-edit RAG (operational dimension only).
  const [editing, setEditing] = useState<EditingRag>(null);

  // CW-A Phase 2 — org-wide total for the scope control's "All facilities (N)"
  // label. Cheap HEAD count, not a row fetch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { count, error: cErr } = await supabase
        .from(showClosed ? 'facilities' : 'active_facilities')
        .select('facility_id', { count: 'exact', head: true });
      if (cancelled) return;
      setAllFacilityCount(cErr ? null : (count ?? null));
    })();
    return () => {
      cancelled = true;
    };
  }, [showClosed, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    // Wait for the assigned set before querying — otherwise the default
    // resolution ('mine' if any, else 'all') would fire against an
    // unresolved scope and flash the wrong list.
    if (!scopeResolved) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Build query — default to Landon + Kansas City for the demo if no
        // employee context is available yet. Column names match the actual
        // public.facilities schema (account_status_rag, facility_address,
        // account_manager_name, status, monthly_rate). The accounts embed
        // resolves via the facilities_account_id_fkey FK constraint, which
        // PostgREST detects through the view since active_facilities is a
        // simple SELECT * FROM facilities WHERE … view (security_invoker).
        //
        // 2026-05-12 repoint: AM list is operational. Closed/Cancelled
        // facilities should never appear on an AM's roster; they live in
        // the Account Management Info tab on facility-detail.tsx, which
        // intentionally still queries the base `facilities` table.
        //
        // 2026-05-12 closed toggle: when the user flips "Show closed" on,
        // swap to the base `facilities` table so closed rows render too.
        // active_facilities is a SECURITY INVOKER view over facilities so
        // the column shape, RLS, and FK embed all stay identical.
        // ---- AFB-0039 fast path: one card-view read (default, non-closed) --
        // v_am_account_cards folds the facility columns + account_name + AM
        // display name + per-facility feedback summary (latest sentiment + open
        // count) into one row per facility — replacing the old facilities +
        // full-feedback-scan (464 rows, client-grouped) + AM-name fetches with a
        // single round trip. The closed ("Show closed") audit mode still uses
        // the base-table path that follows.
        if (!showClosed) {
          let cardQ = supabase.from('v_am_account_cards').select('*');

          // CW-A Phase 2 (2026-08-04): scope is the ONLY thing that narrows
          // this query now — role no longer implies scope. `scope='all'` drops
          // the filter entirely; RLS already permits org-wide read
          // (am_read_all, tcs_employee_read_all). The assigned set is
          // AM-of-record ∪ active route assignments, resolved once in
          // useFacilityScope (the AFB-0033 union, previously duplicated here).
          if (!profile && !employee?.employee_id) {
            // Pre-sign-in demo only: Landon's KC list.
            cardQ = cardQ.eq('account_manager_eid', 'EID-0001').ilike('market_region', '%Kansas%');
          } else if (scope === 'mine' && myFacilityIds.length > 0) {
            cardQ = cardQ.in('facility_id', myFacilityIds);
          }

          // Name order only. RAG order is applied by sortByRagThenName below:
          // `.order('account_status_rag')` sorted the TEXT alphabetically —
          // Amber, Green, Red — which put every Red building last.
          const { data: cardData, error: cardErr } = await cardQ
            .order('facility_name', { ascending: true });
          if (cardErr) throw cardErr;

          const cardRows = (cardData ?? []) as unknown as AmAccountCardRow[];
          // CW-Q Phase 6 — one extra keyed read so the rate cell can say WHY it
          // has no figure. See fetchRateBasisMap for why this isn't a join.
          const cardIds = cardRows.map((r) => r.facility_id).filter((id): id is string => !!id);
          const [cardBasisMap, cardHourlyMap] = await Promise.all([
            fetchRateBasisMap(cardIds),
            fetchHourlyRateMap(cardIds),
          ]);
          const enrichedCards: FacilityWithSentiment[] = cardRows.map((row) => {
            const {
              account_name,
              account_manager_name,
              latest_sentiment,
              open_feedback_count,
              ...facility
            } = row;
            return {
              ...(facility as unknown as Facility),
              account_manager_name: account_manager_name ?? null,
              account_name: account_name ?? null,
              lifecycle: row.lifecycle ?? null,
              latest_sentiment: latest_sentiment ?? null,
              live_rate_basis: cardBasisMap.get(row.facility_id) ?? null,
              live_hourly_rate: cardHourlyMap.get(row.facility_id) ?? null,
              live_open_feedback_count: open_feedback_count ?? 0,
              // v_account_management_cards exposes an OPEN count only — there
              // is no total column on the view. This branch therefore keeps
              // the pre-CW-I behaviour (silent when everything is closed).
              // Noted rather than papered over; fixing it means adding a
              // total_feedback_count to the view, which is a schema change
              // this phase deliberately does not make.
              live_total_feedback_count: open_feedback_count ?? 0,
            };
          });

          if (!cancelled) setFacilities(sortByRagThenName(enrichedCards));
          return;
        }

        const sourceTable = showClosed ? 'facilities' : 'active_facilities';
        let q = supabase.from(sourceTable).select(
          `
          facility_id, facility_name, account_id, facility_type, facility_address,
          state, status, market_region, account_manager_eid,
          planned_start_date, contract_start,
          monthly_rate, account_status_rag,
          rag_set_by, rag_set_at, rag_reason, rag_category,
          latitude, longitude, last_confirmed_at, last_confirmed_by_eid,
          accounts!facilities_account_id_fkey ( account_name )
          /* 2026-07-27: the 2026-05-21 hotfix that selected the legacy
             rag_changed_date / rag_change_reason pair here is removed. That
             comment was stale — active_facilities has exposed all four
             attribution columns since the view was refactored, and reading the
             legacy pair is exactly why the card showed months-old dates and
             reasons while AMs were updating RAG daily. The legacy columns no
             longer exist. rag_set_at / rag_reason are the only source. */
          `,
        );

        // CW-A Phase 2 (2026-08-04) — scope-based, not role-based.
        //   * scope='mine' → the assigned set: AM-of-record ∪ active route
        //     assignments (the AFB-0033 union, Troy/FAC-0060 — a facility on
        //     your route but not yet reassigned to you is still yours to work).
        //     Resolved once in useFacilityScope instead of re-queried here.
        //   * scope='all'  → no filter. RLS enforces read scope; org-wide read
        //     is already permitted for every eligible employee.
        //   * unauthenticated demo → Landon's KC list (used pre-sign-in only)
        // Role no longer implies scope: previously ONLY role==='account_manager'
        // was narrowed, which is why superuser/sales/finance/hr and Jack
        // (national_am) already saw everything and the 5 account_manager-role
        // users could not widen.
        if (scope === 'mine' && myFacilityIds.length > 0 && (profile || employee?.employee_id)) {
          q = q.in('facility_id', myFacilityIds);
        } else if (!profile && !employee?.employee_id) {
          // Pre-sign-in demo: Landon's KC list. Filter by eid (EID-0001 =
          // Landon Kohen) — account_manager_name was retired 2026-06-08 and is
          // no longer a base-table column to filter on.
          q = q
            .eq('account_manager_eid', 'EID-0001')
            .ilike('market_region', '%Kansas%');
        }
        // else: leave q unfiltered — scope='all' is the org-wide view

        // Name order only — same reason as the card path above. Both paths get
        // their RAG order from sortByRagThenName so they cannot disagree.
        const { data, error: fErr } = await q
          .order('facility_name', { ascending: true });

        if (fErr) throw fErr;
        const baseRows = (data ?? []) as unknown as FacilityRowFromDb[];

        // Pull latest feedback per facility AND compute live open-feedback
        // counts. The denormalised counters on `facilities` were dropped on
        // 2026-05-19; this hop is now the only source of truth for both the
        // sentiment pill and the open-feedback count on the card.
        const ids = baseRows.map((f) => f.facility_id);
        const sentimentMap: Record<string, FeedbackSentiment> = {};
        const feedbackCountMap: Record<string, number> = {};
        const totalFeedbackCountMap: Record<string, number> = {};
        const OPEN_FEEDBACK_STATUSES = new Set([
          'Open',
          'In_Progress',
          'Recurring',
        ]);
        if (ids.length > 0) {
          const { data: fb, error: fbErr } = await supabase
            .from('facility_feedback')
            .select('facility_id, sentiment, status, feedback_date')
            .in('facility_id', ids)
            .order('feedback_date', { ascending: false });
          if (fbErr) throw fbErr;
          for (const row of (fb ?? []) as Pick<
            FacilityFeedback,
            'facility_id' | 'sentiment' | 'status' | 'feedback_date'
          >[]) {
            if (!sentimentMap[row.facility_id]) {
              sentimentMap[row.facility_id] = row.sentiment;
            }
            if (OPEN_FEEDBACK_STATUSES.has(row.status)) {
              feedbackCountMap[row.facility_id] =
                (feedbackCountMap[row.facility_id] ?? 0) + 1;
            }
            // CW-I Phase 2. The card used to signal ONLY when something was
            // open, so a facility whose feedback is entirely Closed looked
            // identical to one with no feedback at all — the same Open-only
            // blind spot that hid FBK-0254's four Closed siblings on FAC-0103.
            // Total is what tells you there is something worth opening.
            totalFeedbackCountMap[row.facility_id] =
              (totalFeedbackCountMap[row.facility_id] ?? 0) + 1;
          }
        }

        // Resolve AM display names from account_manager_eid via the canonical
        // v_am_name view (account_manager_name retired 2026-06-08). Works for
        // every role incl. the pre-sign-in demo (v_am_name is anon-readable).
        const amNameMap = await fetchAmNameMap(
          baseRows.map((r) => r.account_manager_eid),
        );

        // CW-Q Phase 6. Note this path is the "Show closed" audit mode, and
        // v_facility_margins excludes Closed and Merged — so closed rows get no
        // map entry and fall back, which is correct: we should not assert a
        // basis for a building the margin chain has stopped reporting on.
        const [basisMap, hourlyMap] = await Promise.all([
          fetchRateBasisMap(ids),
          fetchHourlyRateMap(ids),
        ]);

        // Lifecycle is decided by the database, never here. Closed buildings are
        // not in the card view, so they get no lifecycle and keep the gray pill.
        const lifecycleMap = new Map<string, string | null>();
        if (ids.length > 0) {
          const { data: lc, error: lcErr } = await supabase
            .from('v_account_management_cards')
            .select('facility_id, lifecycle')
            .in('facility_id', ids);
          if (lcErr) throw lcErr;
          for (const r of (lc ?? []) as Array<{ facility_id: string; lifecycle: string | null }>)
            lifecycleMap.set(r.facility_id, r.lifecycle);
        }

        const enriched: FacilityWithSentiment[] = baseRows.map((row) => {
          const { accounts, ...facility } = row;
          return {
            ...(facility as Facility),
            account_manager_name: row.account_manager_eid
              ? amNameMap.get(row.account_manager_eid) ?? null
              : null,
            account_name: accounts?.account_name ?? null,
            lifecycle: lifecycleMap.get(row.facility_id) ?? null,
            latest_sentiment: sentimentMap[row.facility_id] ?? null,
            live_rate_basis: basisMap.get(row.facility_id) ?? null,
            live_hourly_rate: hourlyMap.get(row.facility_id) ?? null,
            live_open_feedback_count: feedbackCountMap[row.facility_id] ?? 0,
            live_total_feedback_count: totalFeedbackCountMap[row.facility_id] ?? 0,
          };
        });

        // Same ordering as the card path — see the note beside its setFacilities.
        if (!cancelled) setFacilities(sortByRagThenName(enriched));
      } catch (e) {
        if (!cancelled) {
          console.error('[my-accounts] load failed', e);
          setError(
            errorMessage(e, 'Failed to load facilities.'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [
    employee?.employee_id,
    profile?.role,
    reloadKey,
    showClosed,
    scope,
    myFacilityIds,
    scopeResolved,
  ]);

  // 2026-05-29 — debounce the raw input ~200ms into `debouncedTerm`. Keeps us
  // from firing a resolver call on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  // 2026-05-29 — drive the alias-aware resolver off the debounced term. We
  // scope to facilities (the list only renders facilities) and let RLS scope
  // the rows to what this user can see; we then intersect with the already-
  // loaded "My Accounts" set in `filtered` so the view never broadens.
  // Empty term → clear matches and fall back to the default unfiltered list.
  // On RPC error we set an empty map (search stays "active") so the local
  // text filter below still works and the box degrades gracefully.
  useEffect(() => {
    const term = debouncedTerm;
    if (!term) {
      setSearchMatches(null);
      setSearchTrunc(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: rpcErr } = await supabase.rpc('search_entities', {
        p_query: term,
        p_entity_types: ['facility'],
        p_limit: 25,
      });
      if (cancelled) return;
      if (rpcErr) {
        console.error('[my-accounts] search_entities failed', rpcErr);
        setSearchMatches(new Map());
        setSearchTrunc(null);
        return;
      }
      const rows = (data ?? []) as SearchEntityRow[];
      const facilityRows = rows.filter((r) => r.entity_type === 'facility');
      const total = facilityRows[0]?.total_matches ?? facilityRows.length;
      setSearchTrunc(
        total > facilityRows.length ? { shown: facilityRows.length, total } : null,
      );
      const map = new Map<string, AliasMatch>();
      for (const row of facilityRows) {
        if (row.entity_type !== 'facility') continue;
        const existing = map.get(row.entity_id);
        // Keep the best (lowest-score) hit per facility, and prefer an alias
        // hit for the hint when scores tie (more informative than "name").
        if (
          !existing ||
          row.score < existing.score ||
          (row.score === existing.score &&
            row.match_kind === 'alias' &&
            existing.matchKind !== 'alias')
        ) {
          map.set(row.entity_id, {
            matchKind: row.match_kind,
            matchedOn: row.matched_on,
            score: row.score,
          });
        }
      }
      setSearchMatches(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedTerm]);

  // Header counts (Red / Amber / Green) — computed from the unfiltered set
  // so the totals don't shrink when the user types into the search box.
  const counts = useMemo(() => {
    let red = 0;
    let amber = 0;
    let green = 0;
    for (const f of facilities) {
      if (f.account_status_rag === 'Red') red++;
      else if (f.account_status_rag === 'Amber') amber++;
      else if (f.account_status_rag === 'Green') green++;
    }
    return { red, amber, green, total: facilities.length };
  }, [facilities]);

  const filtered = useMemo(() => {
    // We drive search off `debouncedTerm` (not the raw input) so the local
    // text filter stays in lock-step with the resolver call that uses the
    // same term.
    const term = debouncedTerm.toLowerCase();
    // Resolve state-name → state-code aliases ("colorado" → "CO") so the
    // AM can type either form. Exact-match against the f.state column.
    const aliased = term ? STATE_ALIASES[term] : undefined;
    const termActive = term.length > 0;

    const rows = facilities.filter((f) => {
      if (ragFilter !== 'all' && f.account_status_rag !== ragFilter)
        return false;
      // Lifecycle chips (Option D PR-5) — additive to the RAG chips.
      if (lifecycleFilter === 'starting_soon' && lensFor(f.lifecycle, f.status) !== 'Starting soon')
        return false;
      if (lifecycleFilter === 'recent' && lensFor(f.lifecycle, f.status) !== 'Recent') return false;
      if (termActive) {
        // (1) Resolver hit — alias-aware, name-aware, RLS-scoped. We INTERSECT
        // with the loaded My Accounts set simply by checking membership here,
        // so the view never shows facilities outside the current scope.
        const resolverHit = searchMatches?.has(f.facility_id) ?? false;
        // (2) Local text fallback — preserves the prior behavior of matching
        // on address / AM name / market region / state-name alias, none of
        // which the facility-name resolver covers. Strictly additive.
        const haystack = [
          f.facility_name,
          f.account_name ?? '',
          f.facility_address ?? '',
          f.account_manager_name ?? '',
          f.state ?? '',
          f.market_region ?? '',
        ]
          .join(' ')
          .toLowerCase();
        const matchesSubstring = haystack.includes(term);
        const matchesStateAlias =
          aliased != null && (f.state ?? '').toUpperCase() === aliased;
        if (!resolverHit && !matchesSubstring && !matchesStateAlias)
          return false;
      }
      return true;
    });

    // When a term is active, order by the resolver's score (ascending: exact
    // before prefix before substring). Resolver hits sort ahead of local-only
    // matches (Infinity); ties fall back to the RAG-then-name order already
    // applied to `facilities` at load (Array.sort is stable, so `i` preserves it).
    if (termActive && searchMatches) {
      return rows
        .map((f, i) => ({ f, i }))
        .sort((a, b) => {
          const sa = searchMatches.get(a.f.facility_id)?.score ?? Infinity;
          const sb = searchMatches.get(b.f.facility_id)?.score ?? Infinity;
          if (sa !== sb) return sa - sb;
          return a.i - b.i;
        })
        .map((x) => x.f);
    }
    return rows;
  }, [facilities, debouncedTerm, ragFilter, lifecycleFilter, searchMatches]);

  const filtersActive =
    ragFilter !== 'all' ||
    lifecycleFilter !== 'all' ||
    search.trim().length > 0;

  // 2026-05-30 — "who's cleaning / who to call" per card. Batched fetch off
  // the loaded facility set (one round-trip for Active assignments +
  // providers); the map is consulted per-card below. Renders progressively —
  // cards show without the lines until this resolves, then fill in.
  const facilityIds = useMemo(
    () => facilities.map((f) => f.facility_id),
    [facilities],
  );
  const { cleaning: cleaningMap } = useFacilitiesCleaning(facilityIds);

  return (
    <div className="mx-auto max-w-6xl p-4 md:p-6">
      <PageHeader
        eyebrow="Accounts"
        title="My Accounts"
        meta={
          loading ? (
            <Skeleton width={220} height={14} />
          ) : (
            <span>
              {filtered.length === counts.total
                ? `${counts.total} ${counts.total === 1 ? 'facility' : 'facilities'}`
                : `${filtered.length} of ${counts.total} facilities`}
              {counts.total > 0 && (
                <>
                  {' · '}
                  <span className="font-medium text-rag-red">
                    {counts.red} red
                  </span>
                  {' · '}
                  <span className="font-medium text-st-warn-fg">
                    {counts.amber} amber
                  </span>
                  {' · '}
                  <span className="font-medium text-rag-green">
                    {counts.green} green
                  </span>
                </>
              )}
            </span>
          )
        }
        actions={<ViewToggle view={view} onChange={setView} />}
      />

      {/* Search + filter chips. Mobile: search on its own row; chips wrap
          below. Desktop: same row. */}
      {searchTrunc && (
        <p className="mt-2 text-xs text-tcs-text-muted">
          Showing {searchTrunc.shown} of {searchTrunc.total} matches — refine your search to narrow it down.
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <SearchBox value={search} onChange={setSearch} />
        {/* CW-A Phase 2 — explicit scope control. Same component and same
            state model as /map, so the two surfaces cannot diverge. */}
        <FacilityScopeControl
          scope={scope}
          onChange={setScope}
          canScopeToMine={canScopeToMine}
          mineCount={myFacilityIds.length}
          allCount={allFacilityCount}
        />
        <FilterChips ragFilter={ragFilter} onChange={setRagFilter} />
        <LifecycleChips value={lifecycleFilter} onChange={setLifecycleFilter} />
        <ShowClosedToggle value={showClosed} onChange={setShowClosed} />
      </div>

      {error && (
        <div className="mt-4">
          <ErrorState
            body={error}
            onRetry={() => setReloadKey((k) => k + 1)}
          />
        </div>
      )}

      <div className="mt-4">
        {view === 'map' ? (
          // WS8 2026-07-31: live map, scoped to the current list filter (same
          // rows the list shows — one definition of "which facilities").
          //
          // CW-I Phase 3a: this consumer passed NO height and got Shell's
          // hardcoded 32rem, which is where the "space below the map" on
          // Accounts came from. It is a full-surface view, not an embedded
          // card, so it gets the flex default with an explicit dvh height.
          // dvh not vh — vh measures without mobile browser chrome and clips
          // the bottom of the map.
          <FacilityMap
            facilityIds={filtered.map((f) => f.facility_id)}
            initialCenter="fit"
            className="h-[calc(100dvh-16rem)] min-h-[24rem]"
          />
        ) : loading ? (
          <FacilityListSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyFacilities
            filtersActive={filtersActive}
            onClear={() => {
              setSearch('');
              setRagFilter('all');
              setLifecycleFilter('all');
            }}
          />
        ) : (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {filtered.map((f) => {
              // Surface the alias hint only when this card matched via an
              // entity alias (e.g. "OPR"); name matches and the default list
              // show nothing extra.
              const match = debouncedTerm
                ? searchMatches?.get(f.facility_id)
                : undefined;
              const aliasHint =
                match?.matchKind === 'alias' ? match.matchedOn : null;
              return (
              <li key={f.facility_id}>
                <FacilityCard
                  facilityName={f.facility_name}
                  facilityId={f.facility_id}
                  facilityType={f.facility_type}
                  addressSummary={shortAddress(f.facility_address)}
                  aliasHint={aliasHint}
                  rag={f.account_status_rag}
                  sentiment={f.latest_sentiment}
                  lifecycle={lensFor(f.lifecycle, f.status)}
                  futureStart={startLabel(f.lifecycle, f.planned_start_date)}
                  region={f.market_region}
                  cleaning={cleaningMap.get(f.facility_id) ?? null}
                  href={`/account-management/facilities/${f.facility_id}`}
                  // 2026-05-21 click-to-edit RAG. Closed facilities skip
                  // the affordance (the dot is hidden by FacilityCard).
                  onRagClick={
                    f.status === 'Closed'
                      ? undefined
                      : () => setEditing({ facilityId: f.facility_id })
                  }
                  meta={
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <FacilityMetaLine
                        am={f.account_manager_name}
                        monthlyRate={f.monthly_rate}
                        rateBasis={f.live_rate_basis ?? null}
                        hourlyRate={f.live_hourly_rate ?? null}
                        openFeedback={f.live_open_feedback_count}
                        totalFeedback={f.live_total_feedback_count}
                      />
                    </span>
                  }
                />
              </li>
              );
            })}
          </ul>
        )}
        {editing && (() => {
          const f = facilities.find((x) => x.facility_id === editing.facilityId);
          if (!f) return null;
          return (
            <RagEditPopover
              open
              facilityId={f.facility_id}
              facilityName={f.facility_name}
              currentRag={f.account_status_rag}
              onClose={() => setEditing(null)}
              onOptimistic={({ rag, category, reason }) => {
                // paint the dot now; if save fails, the refetch in
                // onSaved(false, …) restores the truth.
                setFacilities((prev) =>
                  prev.map((row) => {
                    if (row.facility_id !== editing.facilityId) return row;
                    return {
                      ...row,
                      account_status_rag: rag as RagStatus,
                      rag_category:
                        (category as RagCategory) ?? row.rag_category,
                      rag_reason: reason ?? row.rag_reason,
                    };
                  }),
                );
              }}
              onSaved={(ok) => {
                if (!ok) {
                  // refetch to undo the optimistic UI
                  setReloadKey((k) => k + 1);
                }
              }}
            />
          );
        })()}
      </div>

      {/* Closed Accounts archive — Section B of cowork
          account-mgmt-deep-polish-2026-05-20. Small footer link only;
          intentionally not in the sidebar. */}
      <div className="mt-6 border-t border-tcs-border pt-4 text-right">
        <Link
          to="/account-management/archive"
          className="text-xs text-tcs-text-secondary hover:text-tcs-text"
        >
          View closed accounts archive →
        </Link>
      </div>
    </div>
  );
}

// ----- Sub-components ------------------------------------------------------

function ViewToggle({
  view,
  onChange,
}: {
  view: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="View mode"
      className="flex overflow-hidden rounded-md border border-tcs-border-strong bg-tcs-bg"
    >
      <button
        type="button"
        role="tab"
        aria-selected={view === 'list'}
        onClick={() => onChange('list')}
        className={`inline-flex h-9 items-center gap-1.5 px-3 text-xs font-medium transition-colors ${
          view === 'list'
            ? 'bg-tcs-inverse text-tcs-inverse-ink'
            : 'text-tcs-text-secondary hover:bg-tcs-bg-muted'
        }`}
      >
        <ListIcon className="h-3.5 w-3.5" />
        List
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={view === 'map'}
        onClick={() => onChange('map')}
        className={`inline-flex h-9 items-center gap-1.5 px-3 text-xs font-medium transition-colors ${
          view === 'map'
            ? 'bg-tcs-inverse text-tcs-inverse-ink'
            : 'text-tcs-text-secondary hover:bg-tcs-bg-muted'
        }`}
      >
        <MapIcon className="h-3.5 w-3.5" />
        Map
      </button>
    </div>
  );
}

function SearchBox({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tcs-text-muted" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search facility, account, address, AM…"
        className="h-9 w-72 max-w-full rounded-md border border-tcs-border-strong bg-tcs-bg pl-9 pr-3 text-sm placeholder-tcs-text-muted focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
      />
    </div>
  );
}

function FilterChips({
  ragFilter,
  onChange,
}: {
  ragFilter: RagFilter;
  onChange: (v: RagFilter) => void;
}) {
  const allLabel = 'All RAG';
  const options: Array<{ value: RagFilter; label: string }> = [
    { value: 'all', label: allLabel },
    { value: 'Red', label: 'Red' },
    { value: 'Amber', label: 'Amber' },
    { value: 'Green', label: 'Green' },
  ];
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      aria-label="Filter by Operational RAG"
    >
      {options.map((opt) => {
        const active = ragFilter === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors ${
              active
                ? 'bg-tcs-inverse text-tcs-inverse-ink'
                : 'bg-tcs-surface text-tcs-text-secondary hover:bg-tcs-border'
            }`}
          >
            {opt.value !== 'all' && (
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{
                  // CW-J: was a hard-coded copy of the OLD RAG triad, so this
                  // filter's dots disagreed with the dots on the cards below it.
                  backgroundColor:
                    opt.value === 'Red'
                      ? RAG_COLORS.Red
                      : opt.value === 'Amber'
                        ? RAG_COLORS.Amber
                        : RAG_COLORS.Green,
                }}
                aria-hidden="true"
              />
            )}
            {opt.label}
            {active && opt.value !== 'all' && (
              <X className="h-3 w-3 opacity-70" />
            )}
          </button>
        );
      })}
    </div>
  );
}

function FacilityListSkeleton() {
  return (
    <ul className="grid grid-cols-1 gap-3 md:grid-cols-2" aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <li
          key={i}
          className="rounded-xl border border-tcs-border bg-tcs-bg p-4"
        >
          <div className="flex items-start gap-3">
            <Skeleton width={3} height={48} className="rounded-[3px]" block />
            <div className="flex-1 space-y-2">
              <Skeleton width="70%" height={16} block />
              <Skeleton width="50%" height={12} block />
              <Skeleton width="40%" height={11} block />
            </div>
            <Skeleton width={64} height={20} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyFacilities({
  filtersActive,
  onClear,
}: {
  filtersActive: boolean;
  onClear: () => void;
}) {
  if (filtersActive) {
    return (
      <EmptyState
        title="No facilities match"
        body="Try clearing the search or RAG filter."
        cta={
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        }
      />
    );
  }
  return (
    <EmptyState
      title="No facilities assigned"
      body="When a facility is added to your AM portfolio it will appear here."
    />
  );
}

// 2026-05-12: small two-state toggle styled to live alongside FilterChips.
// "Show closed" Off is the operational default — query targets the
// active_facilities view. On swaps the source to the base facilities table
// so the user can see Closed/Cancelled rows (rendered with a gray pill in
// Lifecycle filter chips (Option D PR-5) — additive to the RAG chips. Surface
// the two NEW ACCOUNTS lenses AMs triage by: Starting Soon and Recent Starts.
function LifecycleChips({
  value,
  onChange,
}: {
  value: LifecycleFilter;
  onChange: (v: LifecycleFilter) => void;
}) {
  const opts: { value: LifecycleFilter; label: string }[] = [
    { value: 'starting_soon', label: 'Starting soon' },
    { value: 'recent', label: 'Recent starts' },
  ];
  return (
    <div className="inline-flex items-center gap-1" role="group" aria-label="Filter by lifecycle">
      {opts.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(active ? 'all' : opt.value)}
            aria-pressed={active}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              active
                ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:border-tcs-border-strong'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// place of the RAG dot — see FacilityCard).
function ShowClosedToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-medium transition-colors ${
        value
          ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
          : 'border-tcs-border-strong bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-bg-muted'
      }`}
      title={
        value
          ? 'Showing closed facilities (querying base facilities table)'
          : 'Closed facilities hidden (querying active_facilities view)'
      }
    >
      <span
        aria-hidden="true"
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          value ? 'bg-tcs-raised' : 'bg-tcs-text-muted'
        }`}
      />
      Show closed
    </button>
  );
}

// ----- Re-exports for legacy callers ----------------------------------------
// `CATEGORY_LABELS` historically lived here; it now lives in design-tokens
// and is re-exported for any caller that still uses the old import path.
// Other previously-local helpers (RagDot, SentimentPill, fmtCurrency,
// shortAddress) are now imported directly from `shared/components` or
// `shared/format` by their consumers.

export { CATEGORY_LABELS } from '../../shared/design-tokens';
